// src/config/equipements.catalogue.ts — SCE Issues
// Catalogue des modèles d'équipement SCE + résolution d'un libellé Windchill.
//
// ─── Rôle ───
//
// Source unique des 55 valeurs de la colonne Choice `field_3` (FamilleEquipement)
// de SCE_Equipements, enrichies de trois axes que SharePoint ne porte pas :
// gamme, type et pression.
//
// 29/09/2026 : Hipersep Flowdrive S, Hipersep Flowdrive M et Resolute BioSC L
// ajoutés (et par Guillaume à la liste de choix SharePoint) — les deux
// derniers étaient en attente depuis le Lot 18 (docs/lot-retours-coordinateurs.md,
// « Modèles à compléter », codes désignation ZIP00M et ZIO00L).
//
// ⚠️ Les libellés doivent correspondre AU CARACTÈRE PRÈS à la liste de choix
//    SharePoint : toute divergence fait rejeter l'écriture. `verifierCatalogue()`
//    compare les deux au moment de l'import et remonte l'écart.
//
// ─── Pourquoi trois axes en code plutôt que trois colonnes SharePoint ───
//
// La gamme (Hipersep / Resolute), le type (pompage / colonne / préparation) et
// la pression (haute / basse) sont entièrement déductibles du modèle. Les
// stocker dans SharePoint créerait trois colonnes à maintenir à la main, donc
// trois occasions de désynchronisation. Ici le dashboard peut filtrer sur
// n'importe quel axe sans une seule colonne de plus.
//
// ─── Pourquoi un résolveur par règles et pas un rapprochement flou ───
//
// Les libellés de fourniture Windchill sont trop irréguliers pour une
// comparaison de chaînes. Échantillon réel :
//
//   "LC1000 CUSTOMIZED (NVS44977)"        → Hipersep Prochrom 1000
//   "LP1600 MODULAR (NVS44996)"           → Resolute Prochrom 1600
//   "Hip Flowdrive XXL custom (NVS50193)" → Hipersep Flowdrive XXL
//   "HIPERSEP BIO XL MODULAR (NVS44994)"  → Resolute Flowdrive XL
//   "HIPERSEP XL MODULAR (NVS44883)"      → Hipersep Flowdrive XL
//   "BIOSC M 4MCC Custom (NVS44969)"      → Resolute BioSC M
//   "Slurry 120 L. (NVS20998)"            → Slurry Unit 120L
//
// Aucune inclusion de sous-chaîne ne rattrape ça. On extrait donc
// (famille, gamme, calibre) par règles, puis on reconstitue le libellé exact.
//
// ─── Codes produit hérités (Novasep) ───
//
//   LC<n> / ZC<n>  → colonne HAUTE pression → Hipersep Prochrom <n>
//   LP<n>          → colonne BASSE pression → Resolute Prochrom <n>
//   "Hipersep Bio" → ancien nom de la gamme Resolute (rebranding Sartorius)

import type { CategorieEquipement } from '@/types/issue.types'

// ─── Axes ───

export type GammeEquipement = 'Hipersep' | 'Resolute' | 'Commun'

export type TypeEquipement =
  | 'Module de pompage'
  | 'Colonne'
  | 'Slurry / PU'

export type PressionEquipement = 'Haute' | 'Basse' | 'Sans objet'

export const TYPES_EQUIPEMENT: readonly TypeEquipement[] = [
  'Module de pompage',
  'Colonne',
  'Slurry / PU',
]

/**
 * Type d'équipement déduit de la catégorie PORTÉE PAR LE PROBLÈME (field_9).
 *
 * Les 5 catégories s'emboîtent exactement dans les 3 types : le type se lit
 * donc sur le problème lui-même, sans jointure avec le référentiel. Un
 * problème dont l'équipement n'a jamais été importé de Windchill a quand même
 * un type — seul son modèle reste inconnu.
 */
export const TYPE_PAR_CATEGORIE: Record<CategorieEquipement, TypeEquipement> = {
  Flowdrive: 'Module de pompage',
  BioSC: 'Module de pompage',
  'Skid Booster': 'Module de pompage',
  Colonne: 'Colonne',
  'Slurry / Packing unit': 'Slurry / PU',
}

export interface ModeleEquipement {
  /** Valeur exacte de field_3 dans SharePoint. */
  libelle: string
  gamme: GammeEquipement
  type: TypeEquipement
  pression: PressionEquipement
  /** Taille / déclinaison : "Pilot", "XXL", "1000", "220L"… */
  calibre: string
  /**
   * Catégorie du problème (field_9) que porte un équipement de ce modèle,
   * c.-à-d. le suffixe de son N° de série : un -SC ne peut être qu'une
   * colonne. Sert de garde-fou au résolveur (cf. `resoudreModele`).
   */
  categorie: CategorieEquipement
}

// ─── Déclinaisons par famille ───
//
// Déclarées en listes plates puis dépliées : 52 objets écrits à la main
// dériveraient au premier ajout.

const HIPERSEP_FLOWDRIVE = [
  'Pilot',
  'S',
  'M',
  'Process M',
  'Process L',
  'Process XL',
  'L',
  'XL',
  'XXL',
] as const

const HIPERSEP_PROCHROM = [
  'PS50', 'PS80', '50', '80', '110', '150', '200',
  '300', '450', '590', '800', '1000', '1200',
] as const

const RESOLUTE_FLOWDRIVE = ['S', 'M', 'L', 'XL'] as const

const RESOLUTE_PROCHROM = [
  '150', '200', '300', '450', '600',
  '800', '1000', '1200', '1400', '1600',
] as const

const RESOLUTE_BIOSC = ['Lab', 'Pilot', 'M', 'L'] as const

const SLURRY_UNIT = ['40L', '60L', '120L', '150L', '220L', '400L', '800L'] as const

const PACKING_UNIT = ['S', 'M', 'L', 'XL'] as const

// ─── Catalogue ───

export const CATALOGUE_EQUIPEMENTS: ModeleEquipement[] = [
  ...HIPERSEP_FLOWDRIVE.map((calibre) => ({
    libelle: `Hipersep Flowdrive ${calibre}`,
    gamme: 'Hipersep' as const,
    type: 'Module de pompage' as const,
    pression: 'Haute' as const,
    calibre,
    categorie: 'Flowdrive' as const,
  })),
  {
    libelle: 'Hipersep Varicol',
    gamme: 'Hipersep',
    type: 'Module de pompage',
    pression: 'Haute',
    calibre: 'Varicol',
    // Suffixe jamais vu dans les exports : rangé avec les modules de pompage (-H).
    categorie: 'Flowdrive',
  },
  ...HIPERSEP_PROCHROM.map((calibre) => ({
    libelle: `Hipersep Prochrom ${calibre}`,
    gamme: 'Hipersep' as const,
    type: 'Colonne' as const,
    pression: 'Haute' as const,
    calibre,
    categorie: 'Colonne' as const,
  })),
  ...RESOLUTE_FLOWDRIVE.map((calibre) => ({
    libelle: `Resolute Flowdrive ${calibre}`,
    gamme: 'Resolute' as const,
    type: 'Module de pompage' as const,
    pression: 'Basse' as const,
    calibre,
    categorie: 'Flowdrive' as const,
  })),
  ...RESOLUTE_PROCHROM.map((calibre) => ({
    libelle: `Resolute Prochrom ${calibre}`,
    gamme: 'Resolute' as const,
    type: 'Colonne' as const,
    pression: 'Basse' as const,
    calibre,
    categorie: 'Colonne' as const,
  })),
  ...RESOLUTE_BIOSC.map((calibre) => ({
    libelle: `Resolute BioSC ${calibre}`,
    gamme: 'Resolute' as const,
    type: 'Module de pompage' as const,
    pression: 'Basse' as const,
    calibre,
    categorie: 'BioSC' as const,
  })),
  {
    libelle: 'Resolute RCC | MU',
    gamme: 'Resolute',
    type: 'Module de pompage',
    pression: 'Basse',
    calibre: 'MU',
    categorie: 'Flowdrive',
  },
  {
    libelle: 'Skid Booster',
    gamme: 'Hipersep',
    type: 'Module de pompage',
    pression: 'Sans objet',
    calibre: 'Booster',
    categorie: 'Skid Booster',
  },
  ...SLURRY_UNIT.map((calibre) => ({
    libelle: `Slurry Unit ${calibre}`,
    gamme: 'Commun' as const,
    type: 'Slurry / PU' as const,
    pression: 'Sans objet' as const,
    calibre,
    categorie: 'Slurry / Packing unit' as const,
  })),
  ...PACKING_UNIT.map((calibre) => ({
    libelle: `Packing Unit ${calibre}`,
    gamme: 'Commun' as const,
    type: 'Slurry / PU' as const,
    pression: 'Sans objet' as const,
    calibre,
    categorie: 'Slurry / Packing unit' as const,
  })),
  {
    libelle: 'Packer',
    gamme: 'Commun',
    type: 'Slurry / PU',
    pression: 'Sans objet',
    calibre: 'Packer',
    categorie: 'Slurry / Packing unit',
  },
]

/** Index libellé → modèle, pour une résolution en O(1). */
const PAR_LIBELLE = new Map(
  CATALOGUE_EQUIPEMENTS.map((m) => [m.libelle, m]),
)

export function modeleParLibelle(libelle: string): ModeleEquipement | null {
  return PAR_LIBELLE.get(libelle.trim()) ?? null
}

// ─── Normalisation ───

/** Une référence NVS, entre parenthèses ou non : « (NVS44977) », « NVS 50173 ». */
const MOTIF_NVS = /\(?\s*NVS\s*(\d{4,6})\s*\)?/i

/** Minuscules sans accents, référence NVS retirée, ponctuation réduite à l'espace. */
function normaliser(texte: string): string {
  return texte
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(new RegExp(MOTIF_NVS.source, 'gi'), ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

/** Retire les zéros de tête d'un calibre numérique ("0450" → "450"). */
function sansZerosDeTete(n: string): string {
  return n.replace(/^0+(?=\d)/, '')
}

// ─── Détection du calibre d'un module de pompage ───
//
// Ordre STRICTEMENT du plus spécifique au plus général : "Process XL" doit
// gagner sur "XL", et "XXL" sur "XL".

const CALIBRES_POMPAGE: { motif: RegExp; calibre: string }[] = [
  { motif: /\bprocess\s+xl\b/, calibre: 'Process XL' },
  { motif: /\bprocess\s+l\b/, calibre: 'Process L' },
  { motif: /\bprocess\s+m\b/, calibre: 'Process M' },
  { motif: /\bpilote?\b/, calibre: 'Pilot' },
  { motif: /\bxxl\b/, calibre: 'XXL' },
  { motif: /\bxl\b/, calibre: 'XL' },
  { motif: /\bl\b/, calibre: 'L' },
  { motif: /\bm\b/, calibre: 'M' },
  { motif: /\bs\b/, calibre: 'S' },
]

function calibrePompage(t: string): string | null {
  for (const { motif, calibre } of CALIBRES_POMPAGE) {
    if (motif.test(t)) return calibre
  }
  return null
}

/**
 * Gamme Resolute si le texte porte l'un de ses marqueurs.
 *
 * ⚠️ "bio" en fait partie : "Hipersep Bio XL" est l'ancien nom commercial de
 * ce qui s'appelle aujourd'hui "Resolute Flowdrive XL".
 */
function estResolute(t: string): boolean {
  return /\bresolute\b/.test(t) || /\bbio\b/.test(t)
}

// ─── Résolveur par la fourniture (texte libre) ───

/**
 * Identifie le modèle catalogue correspondant à un libellé de fourniture
 * Windchill. Retourne null si rien de certain — l'appelant laisse alors
 * field_3 vide plutôt que de risquer un rejet SharePoint.
 *
 * L'ordre des règles compte : les familles à marqueur fort (BioSC, Varicol,
 * Booster, Slurry…) sont testées avant les règles génériques, sans quoi
 * "Hipersep Resolute BioSC Pilot" serait pris pour un Flowdrive Pilot et
 * "HIPERSEP BOOSTER MODULE" pour un module de pompage sans calibre.
 *
 * Utilisé seul, il ne voit que le texte : `resoudreModele` le croise avec la
 * désignation et le suffixe du N° de série.
 */
export function identifierModele(fourniture: string): ModeleEquipement | null {
  const t = normaliser(fourniture)
  if (t === '') return null

  // ─── 1. Familles à marqueur fort ───

  if (/\bbiosc\b/.test(t)) {
    if (/\blab\b/.test(t)) return modeleParLibelle('Resolute BioSC Lab')
    if (/\bpilote?\b/.test(t)) return modeleParLibelle('Resolute BioSC Pilot')
    if (/\bl\b/.test(t)) return modeleParLibelle('Resolute BioSC L')
    if (/\bm\b/.test(t)) return modeleParLibelle('Resolute BioSC M')
    return null
  }

  if (/\brcc\b/.test(t)) return modeleParLibelle('Resolute RCC | MU')
  if (/\bvaricol\b/.test(t)) return modeleParLibelle('Hipersep Varicol')
  if (/\bbooster\b/.test(t)) return modeleParLibelle('Skid Booster')

  // ─── 2. Slurry / PU ───

  const slurry = t.match(/\bslurry\b.*?\b(\d{2,3})\s*l\b/)
  if (slurry) {
    return modeleParLibelle(`Slurry Unit ${sansZerosDeTete(slurry[1] ?? '')}L`)
  }

  // "PUL" / "PUM" sont les codes de désignation des Packing Unit L / M
  const packing = t.match(/\bpacking\s+unit\s+(s|m|l|xl)\b/) ?? t.match(/\bpu(s|m|l)\b/)
  if (packing) {
    return modeleParLibelle(`Packing Unit ${(packing[1] ?? '').toUpperCase()}`)
  }

  if (/\bpacker\b/.test(t)) return modeleParLibelle('Packer')

  // ─── 3. Colonnes ───
  //
  // Deux écritures : le nom commercial ("Prochrom 450", "Prochrom Column 1200",
  // "Prochrom1200", "Prochrom 1000mm") et le code produit hérité Novasep
  // ("LC450", "ZC0450", "LP1600").

  const prochromPs = t.match(/\bprochrom\s+ps\s*(\d{2,4})\b/)
  if (prochromPs) {
    const modele = modeleParLibelle(
      `Hipersep Prochrom PS${sansZerosDeTete(prochromPs[1] ?? '')}`,
    )
    if (modele) return modele
  }

  const prochrom = t.match(/\bprochrom\s*(?:column\s*)?(\d{2,4})(?:\s*mm)?\b/)
  const codeColonne = t.match(/\b(lc|zc|lp)\s*0*(\d{2,4})\b/)

  if (prochrom || codeColonne) {
    const numero = sansZerosDeTete(
      (prochrom ? prochrom[1] : codeColonne?.[2]) ?? '',
    )
    // LP = basse pression, donc gamme Resolute — sinon on lit le texte
    const resolute = codeColonne?.[1] === 'lp' || estResolute(t)
    const gamme = resolute ? 'Resolute' : 'Hipersep'
    return modeleParLibelle(`${gamme} Prochrom ${numero}`)
  }

  // ─── 4. Modules de pompage ───
  //
  // "flowdrive" est le marqueur explicite, mais l'export l'omet souvent
  // ("HIPERSEP XL MODULAR", "HIP XXL cust", "Process M LF cust") : la gamme,
  // son abréviation "hip" ou le mot "process" (les calibres Process
  // n'existent que chez Hipersep) suffisent, à condition qu'un calibre soit
  // identifiable.

  if (/\b(flowdrive|hipersep|hip|resolute|process)\b/.test(t)) {
    const calibre = calibrePompage(t)
    if (calibre === null) return null

    const gamme = estResolute(t) ? 'Resolute' : 'Hipersep'
    return modeleParLibelle(`${gamme} Flowdrive ${calibre}`)
  }

  return null
}

// ─── Résolveur par la désignation (code de configuration) ───
//
// La « Désignation de recherche » Windchill est un code généré, bien plus
// régulier que la fourniture. Relevé sur l'export du 17/09/2026 (324
// équipements confirmés par leur fourniture) :
//
//   LC1000COE… / ZC0450COA…   → Hipersep Prochrom <n>   (LC / ZC : haute pression)
//   LP1000CON… / ZP0450ASN…   → Resolute Prochrom <n>   (LP / ZP : basse pression)
//   PS0050COA…                → Hipersep Prochrom PS<n>
//   HIP / ZIP + calibre       → Hipersep Flowdrive      (ZIPXXLCEA… → XXL)
//   HIB / ZIB + calibre       → Resolute Flowdrive      ("Hipersep Bio")
//   BIO / ZIO + calibre       → Resolute BioSC
//   BOS / ZOS                 → Skid Booster
//   MuRcc…                    → Resolute RCC | MU
//
// Le calibre tient sur 3 caractères après le préfixe. Un calibre inconnu
// (« ZZZ ») ne dit que la famille : la fourniture tranche alors.

/**
 * Calibres des modules de pompage Hipersep. 0PS = Process M « Low Flow ».
 * 0PXL (Process XL, quatre caractères) est traité à part.
 */
const CALIBRES_HIP: Record<string, string> = {
  PIL: 'Pilot',
  '00S': 'S',
  '00M': 'M',
  '0PS': 'Process M',
  '0PM': 'Process M',
  '0PL': 'Process L',
  '00L': 'L',
  '0XL': 'XL',
  XXL: 'XXL',
}

const CALIBRES_HIB: Record<string, string> = { '00S': 'S', '00M': 'M', '00L': 'L', '0XL': 'XL' }

const CALIBRES_BIO: Record<string, string> = { LAB: 'Lab', PIL: 'Pilot', '00M': 'M', '00L': 'L' }

function famille(prefixeLibelle: string): ModeleEquipement[] {
  return CATALOGUE_EQUIPEMENTS.filter((m) => m.libelle.startsWith(prefixeLibelle))
}

function modeleOuFamille(
  prefixeLibelle: string,
  calibres: Record<string, string>,
  code: string,
): ModeleEquipement[] | null {
  const calibre = calibres[code]
  if (calibre === undefined) {
    // Calibre illisible (« ZZZ ») : seule la famille est sûre. Un calibre lisible
    // mais absent du catalogue (« 00M » d'un Hipersep M) ne l'est pas : rien.
    return /^Z+$/.test(code) ? famille(prefixeLibelle) : null
  }
  const m = modeleParLibelle(`${prefixeLibelle}${calibre}`)
  return m ? [m] : null
}

/**
 * Modèles compatibles avec une désignation Windchill ; null si le code n'est
 * pas reconnu (aucune information, pas une contradiction).
 */
export function modelesDepuisDesignation(designation: string): ModeleEquipement[] | null {
  const d = designation.replace(/\s+/g, '').toUpperCase()
  if (d === '') return null

  const colonne = /^(LC|ZC|LP|ZP)0*(\d{2,4})/.exec(d)
  if (colonne) {
    const code = colonne[1] ?? ''
    const numero = colonne[2] ?? ''
    const gamme = code === 'LP' || code === 'ZP' ? 'Resolute' : 'Hipersep'
    const m = modeleParLibelle(`${gamme} Prochrom ${numero}`)
    return m ? [m] : null
  }

  const ps = /^PS0*(\d{2,3})/.exec(d)
  if (ps) {
    const m = modeleParLibelle(`Hipersep Prochrom PS${ps[1] ?? ''}`)
    return m ? [m] : null
  }

  // 0PXL (Process XL) : seul calibre sur QUATRE caractères (Guillaume, 05/10).
  if (/^[HZ]IP0PXL/.test(d)) return modeleOuFamille('Hipersep Flowdrive ', { '0PXL': 'Process XL' }, '0PXL')

  const pompage = /^([HZ])(IP|IB|IO|OS)(.{3})/.exec(d)
  if (pompage) {
    const code = pompage[3] ?? ''
    switch (pompage[2]) {
      case 'IP':
        return modeleOuFamille('Hipersep Flowdrive ', CALIBRES_HIP, code)
      case 'IB':
        return modeleOuFamille('Resolute Flowdrive ', CALIBRES_HIB, code)
      case 'IO':
        return modeleOuFamille('Resolute BioSC ', CALIBRES_BIO, code)
      case 'OS':
        return famille('Skid Booster')
    }
  }
  // « BIOLAB… », « BIOPIL… », « BOSPIL… » : préfixe BIO / BOS
  const bio = /^BIO(LAB|PIL|00M)/.exec(d)
  if (bio) return modeleOuFamille('Resolute BioSC ', CALIBRES_BIO, bio[1] ?? '')
  if (/^BOS/.test(d)) return famille('Skid Booster')

  if (/^MURCC/.test(d)) return famille('Resolute RCC')

  return null
}

// ─── Résolveur complet : désignation × fourniture × suffixe ───

/** La fourniture découpée par référence NVS : une ligne Windchill en porte parfois deux. */
function segmentsFourniture(fourniture: string): string[] {
  const morceaux = fourniture.split(new RegExp(`(${MOTIF_NVS.source})`, 'i'))
  // split avec groupes capturants : [texte, nvs complet, chiffres, texte, …]
  const segments: string[] = []
  for (let k = 0; k < morceaux.length; k += 3) {
    const texte = `${morceaux[k] ?? ''}${morceaux[k + 1] ?? ''}`
    if (texte.trim() !== '') segments.push(texte)
  }
  return segments.length ? segments : [fourniture]
}

function nvsDuSegment(segment: string): string {
  const m = MOTIF_NVS.exec(segment)
  return m ? `NVS${m[1] ?? ''}` : ''
}

/**
 * La référence NVS de l'équipement, lue dans sa fourniture : « NVS50312 ».
 * Vide si absente. Quand la ligne en porte plusieurs (un Flowdrive et sa
 * colonne sur la même cellule), c'est celle dont le texte correspond à la
 * catégorie de l'équipement.
 *
 * C'est aussi le numéro du schéma électrique (EDE « NVS50312-G ») : deux
 * équipements de même NVS le partagent.
 */
export function nvsDeFourniture(
  fourniture: string | undefined,
  categorie: CategorieEquipement | null,
): string {
  if (!fourniture) return ''
  const avecNvs = segmentsFourniture(fourniture).filter((s) => nvsDuSegment(s) !== '')
  if (avecNvs.length <= 1) return avecNvs[0] ? nvsDuSegment(avecNvs[0]) : ''
  const compatibles = avecNvs.filter((s) => {
    const m = identifierModele(s)
    return m !== null && (categorie === null || m.categorie === categorie)
  })
  return compatibles.length === 1 && compatibles[0] ? nvsDuSegment(compatibles[0]) : ''
}

export type SourceModele = 'accord' | 'designation' | 'fourniture' | 'nvs'

export interface ResolutionModele {
  modele: ModeleEquipement | null
  /** D'où vient le modèle : les deux sources d'accord, une seule, ou le même NVS. */
  source?: SourceModele
  /** Présent quand les sources se contredisent : à vérifier dans l'aperçu. */
  alerte?: string
}

function intersection(a: ModeleEquipement[], b: ModeleEquipement[]): ModeleEquipement[] {
  return a.filter((m) => b.includes(m))
}

/**
 * Le modèle d'un équipement, à partir de TOUT ce que l'export en dit.
 *
 * Chaque source réduit la liste des modèles possibles :
 *  - le **suffixe** du N° de série (la catégorie) : un -SC n'est qu'une colonne ;
 *  - la **désignation** : code de configuration, le plus fiable ;
 *  - la **fourniture** : texte libre, découpé par NVS s'il en porte plusieurs.
 *
 * Un seul modèle restant → retenu. Désignation et fourniture en désaccord →
 * la désignation l'emporte, avec une alerte. Rien de sûr → null : le champ
 * reste vide plutôt que faux.
 */
export function resoudreModele(entree: {
  fourniture: string
  designation: string
  categorie: CategorieEquipement | null
}): ResolutionModele {
  const { categorie } = entree
  const compatible = (m: ModeleEquipement): boolean => categorie === null || m.categorie === categorie

  const parDesignation =
    modelesDepuisDesignation(entree.designation) ??
    // Désignation écrite en clair (« Packing unit M », « SLURRY UNIT 60L », « PUL »)
    (() => {
      const m = identifierModele(entree.designation)
      return m ? [m] : null
    })()
  const D = parDesignation?.filter(compatible) ?? null

  const brutsFourniture = segmentsFourniture(entree.fourniture)
    .map((s) => identifierModele(s))
    .filter((m): m is ModeleEquipement => m !== null)
  const F = brutsFourniture.length ? [...new Set(brutsFourniture.filter(compatible))] : null

  // Une source qui ne propose QUE des modèles d'une autre catégorie (un
  // « Packer » sur un -SC) : on ne la suit pas, et on le signale.
  const horsCategorie = (bruts: ModeleEquipement[] | null, nom: string): string | undefined =>
    bruts && bruts.length > 0 && !bruts.some(compatible)
      ? `${nom} « ${bruts[0]?.libelle} » incompatible avec un N° de série ${categorie ?? ''}`
      : undefined
  const incoherent =
    horsCategorie(parDesignation, 'désignation') ?? horsCategorie(brutsFourniture, 'fourniture')

  const seul = (l: ModeleEquipement[] | null): ModeleEquipement | null => (l && l.length === 1 ? (l[0] ?? null) : null)

  if (D && D.length && F && F.length) {
    const commun = intersection(D, F)
    if (commun.length === 1) return { modele: commun[0] ?? null, source: 'accord' }
    if (commun.length > 1) return { modele: null }
    const d = seul(D)
    const f = seul(F)
    if (d) {
      return {
        modele: d,
        source: 'designation',
        alerte: `désignation « ${d.libelle} », fourniture « ${f?.libelle ?? F.map((m) => m.libelle).join(' / ')} »`,
      }
    }
    return f ? { modele: f, source: 'fourniture' } : { modele: null }
  }

  const d = seul(D && D.length ? D : null)
  if (d) return { modele: d, source: 'designation', ...(incoherent ? { alerte: incoherent } : {}) }
  const f = seul(F && F.length ? F : null)
  if (f) return { modele: f, source: 'fourniture', ...(incoherent ? { alerte: incoherent } : {}) }
  return { modele: null, ...(incoherent ? { alerte: incoherent } : {}) }
}

// ─── Contrôle de cohérence avec SharePoint ───

export interface EcartCatalogue {
  /** Présents dans le catalogue, absents de la liste de choix SharePoint. */
  absentsDeSharePoint: string[]
  /** Présents dans SharePoint, absents du catalogue. */
  absentsDuCatalogue: string[]
}

/**
 * Compare le catalogue à la liste de choix réelle de field_3.
 *
 * Un libellé absent de SharePoint ferait échouer l'écriture ; un libellé absent
 * du catalogue signale que quelqu'un a ajouté une valeur côté SharePoint sans
 * mettre le code à jour. Les deux méritent d'être vus à l'import.
 */
export function verifierCatalogue(choixSharePoint: string[]): EcartCatalogue {
  const dansSharePoint = new Set(choixSharePoint.map((c) => c.trim()))
  const dansCatalogue = new Set(CATALOGUE_EQUIPEMENTS.map((m) => m.libelle))

  return {
    absentsDeSharePoint: [...dansCatalogue]
      .filter((l) => !dansSharePoint.has(l))
      .sort(),
    absentsDuCatalogue: [...dansSharePoint]
      .filter((l) => !dansCatalogue.has(l))
      .sort(),
  }
}
